import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-flexbox-grid';
import { translate } from 'react-i18next';
import Spin from 'antd/lib/spin';
import Tag from 'antd/lib/tag';
import Button from 'antd/lib/button';
import message from 'antd/lib/message';
import { Link } from 'react-router';
import { formatDate, formatDateTime } from '../../../common/utils/helpers';
import { loanStatusT } from '../../../common/enums';
import Table from 'antd/lib/table';
import { ResponsiveTable } from '../../../common/components/data-display';
import AccountForm from './AccountForm';

const Column = Table.Column;

export const UNKNOWN = 'neznáme';

const platformLabels = { android: 'Android', ios: 'iOS' };

export const platformLabel = platform => platformLabels[platform] || platform || UNKNOWN;

export const formatDeviceDate = date => (date ? formatDateTime(date, 'DD.MM.YYYY HH:mm') : UNKNOWN);

const orUnknown = value => (value === null || value === undefined || value === '' ? UNKNOWN : value);

const yesNo = value => (value === null || value === undefined ? UNKNOWN : value ? 'áno' : 'nie');

const copyWithTextarea = text => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch (error) {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

const writeToClipboard = text => (navigator.clipboard
  ? navigator.clipboard.writeText(text).then(() => true, () => copyWithTextarea(text))
  : Promise.resolve(copyWithTextarea(text)));

const copyInstallId = installId => writeToClipboard(installId).then(copied => {
  if (copied) message.success('ID inštalácie bolo skopírované.');
  else message.error('ID inštalácie sa nepodarilo skopírovať.');
});

const deviceColumns = [
  { title: 'Zariadenie', dataIndex: 'deviceName', render: orUnknown },
  { title: 'Platforma', dataIndex: 'platform', render: platform => <Tag>{platformLabel(platform)}</Tag> },
  { title: 'Verzia OS', dataIndex: 'osVersion', render: orUnknown },
  { title: 'Jazyk', dataIndex: 'appLanguage', render: orUnknown },
  {
    title: 'ID inštalácie',
    dataIndex: 'installId',
    render: installId => (installId ? (
      <span>
        <code>{installId.slice(0, 8)}…</code>
        <Button size="small" style={{ marginLeft: 4 }} onClick={() => copyInstallId(installId)}>Kopírovať</Button>
      </span>
    ) : UNKNOWN),
  },
  {
    title: 'Stav',
    dataIndex: 'active',
    render: active => (active ? <Tag color="green">aktívne</Tag> : <Tag color="red">deaktivované</Tag>),
  },
  { title: 'Pridané', dataIndex: 'createdAt', render: formatDeviceDate },
  { title: 'Posledná aktivita', dataIndex: 'lastActiveAt', render: formatDeviceDate },
  { title: 'Push token uložený', dataIndex: 'hasPushToken', render: yesNo },
  { title: 'PIN nastavený', dataIndex: 'hasPin', render: yesNo },
  { title: 'Biometria zapnutá', dataIndex: 'hasBiometric', render: yesNo },
];

const AccountDevices = ({ devices, devicesLoading, devicesError, onRetryDevices }) => (
  <section data-testid="account-devices">
    <h2>Zariadenia</h2>
    {devicesError ? (
      <div>
        Zariadenia sa nepodarilo načítať.{' '}
        <Button size="small" onClick={onRetryDevices}>Skúsiť znova</Button>
      </div>
    ) : (
      <ResponsiveTable
        dataSource={devices}
        rowKey="id"
        size="small"
        pagination={false}
        loading={devicesLoading}
        scroll={{ x: true }}
        locale={{ emptyText: 'Účet zatiaľ nemá aktivované žiadne zariadenie.' }}
      >
        {deviceColumns.map(column => <Column key={column.dataIndex} {...column} />)}
      </ResponsiveTable>
    )}
  </section>
);

AccountDevices.propTypes = {
  devices: PropTypes.array.isRequired,
  devicesLoading: PropTypes.bool,
  devicesError: PropTypes.bool,
  onRetryDevices: PropTypes.func,
};

export const AccountDetail = ({
  account,
  isEditable,
  isUpdating,
  onAcceptDocuments,
  onEdit,
  onRejectDocuments,
  onSubmit,
  isDebtor,
  isAdmin,
  t,
  accountUpdateLogs,
  sameInstallAccounts,
  devices,
  devicesLoading,
  devicesError,
  onRetryDevices
}) => (
  <Row className="padded">
    <Col xs={12}>
      <h2>{t('Základné údaje')}</h2>
      <Spin spinning={isUpdating}>
        <AccountForm
          account={account}
          isEditable={isEditable}
          onAcceptDocuments={onAcceptDocuments}
          onEdit={onEdit}
          onRejectDocuments={onRejectDocuments}
          onSubmit={onSubmit}
          isDebtor={isDebtor}
          isAdmin={isAdmin}
          accountUpdateLogs={accountUpdateLogs}

        />
      </Spin>
      {isAdmin && isDebtor && (
        <AccountDevices
          devices={devices}
          devicesLoading={devicesLoading}
          devicesError={devicesError}
          onRetryDevices={onRetryDevices}
        />
      )}
      {isAdmin && isDebtor && sameInstallAccounts.length > 0 && (
        <section data-testid="same-install-accounts">
          <h2>Iné účty z rovnakého telefónu</h2>
          <ul>
            {sameInstallAccounts.map(other => (
              <li key={other.id}>
                <Link to={`/admin/accounts/${other.id}/profile`}>{other.nameFirst} {other.nameLast}</Link>
                {' · '}{formatDate(other.createdAt)}
                {other.latestLoanStatus && ` · ${loanStatusT[other.latestLoanStatus] || other.latestLoanStatus}`}
              </li>
            ))}
          </ul>
        </section>
      )}
    </Col>
  </Row>
);

AccountDetail.propTypes = {
  account: PropTypes.object.isRequired,
  isEditable: PropTypes.bool.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  onAcceptDocuments: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRejectDocuments: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isDebtor: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  accountUpdateLogs: PropTypes.object,
  sameInstallAccounts: PropTypes.array.isRequired,
  devices: PropTypes.array,
  devicesLoading: PropTypes.bool,
  devicesError: PropTypes.bool,
  onRetryDevices: PropTypes.func,
};

AccountDetail.defaultProps = {
  devices: [],
};

export default translate()(AccountDetail);



// WEBPACK FOOTER //
// ./src/features/account-management/components/AccountDetail.js